import type {
	TContentPart,
	TInputResponseMessageRequest,
	TMessage,
	TPaulInputOption,
	TPaulInputResponse,
	TPaulInputSubmission,
} from "../types/agent";
import type { TLitheQuestion } from "../types/lithe.type";

type TOptionsInputSpec = {
	questionId: string;
	inputKind: "options";
	content: string;
	options: Array<TPaulInputOption>;
	minSelections: number;
	maxSelections: number;
};

type TFreeformInputSpec = {
	questionId: string;
	inputKind: "freeform";
	content: string;
	answerType: "text" | "float";
	constraints: TNormalizedInputConstraints;
};

type TNormalizedInputConstraints = {
	min?: number;
	max?: number;
	decimal_digits?: number;
};

export type TPaulInputSpec =
	| TOptionsInputSpec
	| TFreeformInputSpec;

const BOOLEAN_TRUE_VALUES = new Set([
	"1",
	"true",
	"yes",
	"y",
]);
const BOOLEAN_FALSE_VALUES = new Set([
	"0",
	"false",
	"no",
	"n",
]);

const getBooleanOptionValue = (
	option: TPaulInputOption
): boolean | null => {
	for (const value of [option.label, option.id]) {
		const normalized = value.trim().toLowerCase();
		if (BOOLEAN_TRUE_VALUES.has(normalized)) return true;
		if (BOOLEAN_FALSE_VALUES.has(normalized)) return false;
	}

	return null;
};

export const isBooleanPaulOptionSet = (
	options: Array<TPaulInputOption>
): boolean => {
	if (options.length !== 2) return false;

	const values = options.map(getBooleanOptionValue);
	return values.includes(true) && values.includes(false);
};

export const getPaulOptionDisplayLabel = (
	option: TPaulInputOption,
	options: Array<TPaulInputOption>
) => {
	if (!isBooleanPaulOptionSet(options)) return option.label;

	const booleanValue = getBooleanOptionValue(option);
	if (booleanValue === true) return "Yes";
	if (booleanValue === false) return "No";
	return option.label;
};

export const buildPaulInputResponseRequest = ({
	convId,
	module,
	step,
	response,
}: {
	convId: string;
	module: TInputResponseMessageRequest["module"];
	step?: TInputResponseMessageRequest["step"];
	response: TPaulInputResponse;
}): TInputResponseMessageRequest => ({
	conv_id: convId,
	module,
	...(step && { step }),
	query_type: "input_response",
	query_json: response,
});

const isFiniteNumber = (
	value: unknown
): value is number => {
	return (
		typeof value === "number" && Number.isFinite(value)
	);
};

const isNonNegativeInteger = (
	value: unknown
): value is number => {
	return (
		isFiniteNumber(value) &&
		Number.isInteger(value) &&
		value >= 0
	);
};

const normalizeConstraints = (
	constraints: TContentPart["constraints"]
): TNormalizedInputConstraints => {
	if (!constraints) {
		return {};
	}

	return {
		...(isFiniteNumber(constraints.min)
			? { min: constraints.min }
			: {}),
		...(isFiniteNumber(constraints.max)
			? { max: constraints.max }
			: {}),
		...(isNonNegativeInteger(constraints.decimal_digits)
			? { decimal_digits: constraints.decimal_digits }
			: {}),
	};
};

const normalizePaulInputText = (value: string) =>
	value
		.toLowerCase()
		.replace(/[’‘]/g, "'")
		.replace(/\s+/g, " ")
		.trim();

export const parsePaulInputSpec = (
	part: TContentPart
): TPaulInputSpec | null => {
	if (
		part.msg_type !== "input" ||
		typeof part.question_id !== "string" ||
		part.question_id.trim().length === 0 ||
		typeof part.content !== "string"
	) {
		return null;
	}

	if (part.input_kind === "options") {
		if (!Array.isArray(part.options)) {
			return null;
		}

		const options = part.options.filter(
			(option): option is TPaulInputOption =>
				typeof option.id === "string" &&
				option.id.length > 0 &&
				typeof option.label === "string"
		);
		const optionIds = new Set(
			options.map((option) => option.id)
		);
		if (
			options.length === 0 ||
			optionIds.size !== options.length
		) {
			return null;
		}

		const isBoolean = isBooleanPaulOptionSet(options);
		const minSelections = isBoolean
			? 1
			: isNonNegativeInteger(part.min_selections)
				? part.min_selections
				: 1;
		const maxSelections = isBoolean
			? 1
			: isNonNegativeInteger(part.max_selections)
				? part.max_selections
				: options.length;

		if (
			maxSelections < 1 ||
			minSelections > maxSelections ||
			maxSelections > options.length
		) {
			return null;
		}

		return {
			questionId: part.question_id,
			inputKind: "options",
			content: part.content,
			options,
			minSelections,
			maxSelections,
		};
	}

	if (
		part.input_kind === "freeform" &&
		(part.answer_type === "text" ||
			part.answer_type === "float")
	) {
		const constraints = normalizeConstraints(
			part.constraints
		);
		if (
			constraints.min !== undefined &&
			constraints.max !== undefined &&
			constraints.min > constraints.max
		) {
			return null;
		}

		return {
			questionId: part.question_id,
			inputKind: "freeform",
			content: part.content,
			answerType: part.answer_type,
			constraints,
		};
	}

	return null;
};

export const isPaulColumnSelectionInput = (
	part: TContentPart
): boolean => {
	const spec = parsePaulInputSpec(part);
	if (
		!spec ||
		spec.inputKind !== "freeform" ||
		spec.answerType !== "text"
	) {
		return false;
	}

	const normalizedContent = spec.content
		.toLowerCase()
		.replace(/[_-]+/g, " ");
	return (
		/\b(?:columns?|cols?)\b/.test(normalizedContent) &&
		/\b(?:ignore|exclude|select|choose|list)\b|comma\s+separated/.test(
			normalizedContent
		)
	);
};

const getPaulInputKey = (part: TContentPart) => {
	const spec = parsePaulInputSpec(part);
	return spec
		? `${spec.inputKind}:${spec.content.trim().toLowerCase()}`
		: null;
};

const sortPaulMessages = (messages: Array<TMessage>) =>
	[...messages].sort((messageA, messageB) => {
		if (messageA.turn_idx !== messageB.turn_idx) {
			return messageA.turn_idx - messageB.turn_idx;
		}
		if (messageA.time_millis !== messageB.time_millis) {
			return messageA.time_millis - messageB.time_millis;
		}
		if (messageA.role !== messageB.role) {
			return messageA.role === "user" ? -1 : 1;
		}
		return messageA.msg_id.localeCompare(messageB.msg_id);
	});

export const getPendingPaulInputParts = (
	messages: Array<TMessage>
): Array<TContentPart> => {
	const inputKeyByQuestionId = new Map<string, string>();
	let pendingInputs: Array<TContentPart> = [];

	for (const message of sortPaulMessages(messages)) {
		if (message.role === "model" && !message.error_msg) {
			const inputParts = message.content.filter(
				(part) => parsePaulInputSpec(part) !== null
			);
			if (inputParts.length > 0) {
				pendingInputs = inputParts;
				inputParts.forEach((part) => {
					const spec = parsePaulInputSpec(part);
					const inputKey = getPaulInputKey(part);
					if (spec && inputKey) {
						inputKeyByQuestionId.set(
							spec.questionId,
							inputKey
						);
					}
				});
			}
		}

		for (const part of message.content) {
			if (
				part.msg_type === "input_response" &&
				part.is_valid === true &&
				typeof part.question_id === "string"
			) {
				const inputKey = inputKeyByQuestionId.get(
					part.question_id
				);
				pendingInputs = pendingInputs.filter(
					(pendingPart) => {
						const spec =
							parsePaulInputSpec(pendingPart);
						return (
							spec?.questionId !== part.question_id &&
							(!inputKey ||
								getPaulInputKey(pendingPart) !==
									inputKey)
						);
					}
				);
			}
		}
	}

	return pendingInputs;
};

export const getPendingPaulInputQuestionIds = (
	messages: Array<TMessage>
): Set<string> => {
	return new Set(
		getPendingPaulInputParts(messages).flatMap((part) => {
			const spec = parsePaulInputSpec(part);
			return spec ? [spec.questionId] : [];
		})
	);
};

export const getPendingPaulOptionInputParts = (
	messages: Array<TMessage>
): Array<TContentPart> =>
	getPendingPaulInputParts(messages).filter(
		(part) =>
			parsePaulInputSpec(part)?.inputKind === "options"
	);

export const getLatestPaulInputParts = (
	messages: Array<TMessage>
): Array<TContentPart> => {
	let latestInputParts: Array<TContentPart> = [];

	for (const message of sortPaulMessages(messages)) {
		if (message.role === "model" && !message.error_msg) {
			const inputParts = message.content.filter(
				(part) => part.msg_type === "input"
			);
			if (inputParts.length > 0) {
				latestInputParts = inputParts;
			}
		}

		for (const part of message.content) {
			if (
				part.msg_type === "input_response" &&
				part.is_valid === true &&
				typeof part.question_id === "string"
			) {
				latestInputParts = latestInputParts.filter(
					(inputPart) =>
						inputPart.question_id !== part.question_id
				);
			}
		}
	}

	return latestInputParts;
};

export const createPaulOptionInputPartFromQuestion = (
	question: TLitheQuestion
): TContentPart | null => {
	if (
		question.answerType !== "option_selection" ||
		!Array.isArray(question.answers)
	) {
		return null;
	}

	const options = question.answers.flatMap((rawOption) => {
		if (
			typeof rawOption !== "object" ||
			rawOption === null
		) {
			return [];
		}

		const option = rawOption as {
			optionId?: unknown;
			optionName?: unknown;
		};
		if (
			typeof option.optionId !== "string" ||
			option.optionId.length === 0 ||
			typeof option.optionName !== "string"
		) {
			return [];
		}

		return [
			{
				id: option.optionId,
				label: option.optionName,
			},
		];
	});
	if (options.length === 0) {
		return null;
	}

	const isBoolean = isBooleanPaulOptionSet(options);
	const isMultiSelect =
		!isBoolean &&
		(Array.isArray(question.answer) ||
			Array.isArray(question.defaultAnswer));

	return {
		blob_link: null,
		content: question.question,
		extension: "",
		is_large_file: false,
		msg_type: "input",
		question_id: question.questionId,
		input_kind: "options",
		options_source: "dynamic",
		options,
		min_selections: isMultiSelect ? 0 : 1,
		max_selections: isMultiSelect ? options.length : 1,
	};
};

export const getActiveLitheOptionInputPart = ({
	questions,
	pendingChatParts,
	chatOptionParts,
	latestChatInputParts,
}: {
	questions: Array<TLitheQuestion>;
	pendingChatParts: Array<TContentPart>;
	chatOptionParts: Array<TContentPart>;
	latestChatInputParts: Array<TContentPart>;
}): TContentPart | null => {
	const activeQuestion = questions.find(
		(question) =>
			!question.isAnswered || !question.isValidAnswer
	);
	const chatOptionPartsForQuestion =
		chatOptionParts.length > 0
			? chatOptionParts
			: pendingChatParts.filter(
					(part) =>
						parsePaulInputSpec(part)?.inputKind ===
						"options"
				);

	// The chat input is already answerable even when the step's persisted
	// question metadata uses a broader type (for example,
	// risk_factor_breakpoints). Do not hide its picker while waiting for that
	// metadata to catch up.
	if (activeQuestion?.answerType !== "option_selection") {
		return chatOptionPartsForQuestion[0] ?? null;
	}

	const normalizedQuestion = normalizePaulInputText(
		activeQuestion.question
	);
	const matchingChatPart =
		latestChatInputParts.find(
			(part) =>
				part.question_id === activeQuestion.questionId
		) ??
		latestChatInputParts.find(
			(part) =>
				normalizePaulInputText(part.content) ===
				normalizedQuestion
		) ??
		chatOptionPartsForQuestion.find(
			(part) =>
				part.question_id === activeQuestion.questionId
		) ??
		chatOptionPartsForQuestion.find(
			(part) =>
				normalizePaulInputText(part.content) ===
				normalizedQuestion
		) ??
		(latestChatInputParts.length === 1
			? latestChatInputParts[0]
			: null);
	// Step metadata can arrive before Paul's message. Do not synthesize a
	// prompt from that metadata; the question becomes answerable only after
	// the matching input has been emitted in the conversation.
	if (!matchingChatPart) return null;

	// When Paul's turn already carries literal options, those are what the
	// input-response endpoint validates the selected ids against. The step
	// question's catalog options (ids = raw values, e.g. a column name) must
	// not be merged over them, or every click is rejected while typing the
	// label still works.
	const matchingChatSpec = parsePaulInputSpec(matchingChatPart);
	if (
		matchingChatSpec?.inputKind === "options" &&
		matchingChatPart.options_source !== "dynamic"
	) {
		return matchingChatPart;
	}

	const optionInputPart =
		createPaulOptionInputPartFromQuestion(activeQuestion);
	if (!optionInputPart) {
		return matchingChatSpec?.inputKind === "options"
			? matchingChatPart
			: null;
	}

	// Dynamic choices live in the step-question response, while Paul's turn
	// can contain only the input shell. Merge the choices into that shell so
	// the control stays attached to the actual conversation question and uses
	// the question id accepted by the input-response endpoint.
	return {
		...matchingChatPart,
		...optionInputPart,
		content: matchingChatPart.content,
		question_id:
			matchingChatPart.question_id ??
			optionInputPart.question_id,
	};
};

// Maps question_id -> display-ready answer text, for pickers the user has validly answered.
// The server pre-resolves answer_display (dynamic option labels are gone on reload), so this
// needs no id->label mapping and no dependency on the picker's own options.
export const getPaulInputAnswers = (
	messages: Array<TMessage>
): Map<string, string> => {
	const answers = new Map<string, string>();
	for (const message of messages) {
		for (const part of message.content) {
			if (
				part.msg_type === "input_response" &&
				part.is_valid === true &&
				typeof part.question_id === "string"
			) {
				answers.set(
					part.question_id,
					part.answer_display ?? ""
				);
			}
		}
	}
	return answers;
};

export const createPaulInputResponsePart = (
	questionId: string,
	answerDisplay: string
): TContentPart => ({
	msg_type: "input_response",
	content: "",
	blob_link: null,
	extension: "",
	is_large_file: false,
	question_id: questionId,
	answer_display: answerDisplay,
	is_valid: true,
});

export const isPaulInputResponseMessage = (
	message: TMessage
): boolean =>
	message.role === "user" &&
	message.content.some(
		(part) => part.msg_type === "input_response"
	);

export const formatPaulInputResponse = (
	spec: TPaulInputSpec,
	response: TPaulInputResponse
): string => {
	if (
		spec.inputKind === "options" &&
		response.input_kind === "options"
	) {
		const labelsById = new Map(
			spec.options.map((option) => [
				option.id,
				getPaulOptionDisplayLabel(option, spec.options),
			])
		);
		const labels = response.selected
			.map(
				(optionId) => labelsById.get(optionId) ?? optionId
			)
			.join(", ");
		return labels.length > 0 ? labels : "No selection";
	}

	if (
		spec.inputKind === "freeform" &&
		response.input_kind === "freeform"
	) {
		return String(response.value) || "No response";
	}

	return "Response submitted";
};

export const createPaulFreeformSubmission = (
	spec: TFreeformInputSpec,
	rawValue: string
): {
	submission: TPaulInputSubmission | null;
	error: string | null;
} => {
	const trimmedValue = rawValue.trim();
	if (trimmedValue.length === 0) {
		return { submission: null, error: "Enter a response." };
	}

	if (spec.answerType === "text") {
		return {
			submission: {
				response: {
					question_id: spec.questionId,
					input_kind: "freeform",
					answer_type: "text",
					value: trimmedValue,
				},
				displayValue: trimmedValue,
			},
			error: null,
		};
	}

	const value = Number(trimmedValue);
	if (!Number.isFinite(value)) {
		return {
			submission: null,
			error: "Enter a valid number.",
		};
	}
	if (
		spec.constraints.min !== undefined &&
		value < spec.constraints.min
	) {
		return {
			submission: null,
			error: `Enter a value of at least ${spec.constraints.min}.`,
		};
	}
	if (
		spec.constraints.max !== undefined &&
		value > spec.constraints.max
	) {
		return {
			submission: null,
			error: `Enter a value no greater than ${spec.constraints.max}.`,
		};
	}
	if (
		spec.constraints.decimal_digits !== undefined &&
		Math.abs(
			value -
				Number(
					value.toFixed(spec.constraints.decimal_digits)
				)
		) > 1e-9
	) {
		return {
			submission: null,
			error: `Use no more than ${spec.constraints.decimal_digits} decimal places.`,
		};
	}

	return {
		submission: {
			response: {
				question_id: spec.questionId,
				input_kind: "freeform",
				answer_type: "float",
				value,
			},
			displayValue: String(value),
		},
		error: null,
	};
};

export const createPaulOptionsSubmission = (
	spec: TPaulInputSpec,
	rawValue: string
): {
	submission: TPaulInputSubmission | null;
	error: string | null;
} => {
	if (spec.inputKind !== "options") {
		return {
			submission: null,
			error: "This question does not accept an option.",
		};
	}

	const trimmedValue = rawValue.trim();
	if (!trimmedValue) {
		return { submission: null, error: "Enter a response." };
	}

	const normalizedValue = trimmedValue.toLowerCase();
	const booleanOptions = isBooleanPaulOptionSet(
		spec.options
	);
	const positiveOption = spec.options.find(
		(option) => getBooleanOptionValue(option) === true
	);
	const negativeOption = spec.options.find(
		(option) => getBooleanOptionValue(option) === false
	);
	const selectionValues =
		spec.maxSelections === 1
			? [
					booleanOptions &&
					/^(?:yes|y|true)\b/.test(normalizedValue)
						? (positiveOption?.id ?? trimmedValue)
						: booleanOptions &&
							  /^(?:no|n|false)\b/.test(normalizedValue)
							? (negativeOption?.id ?? trimmedValue)
							: trimmedValue,
				]
			: trimmedValue.split(",");
	const selectedOptions = selectionValues.map(
		(selectionValue) => {
			const normalizedSelection = selectionValue
				.trim()
				.toLowerCase();
			return spec.options.find(
				(option) =>
					option.id.trim().toLowerCase() ===
						normalizedSelection ||
					option.label.trim().toLowerCase() ===
						normalizedSelection
			);
		}
	);

	if (
		selectedOptions.some((option) => option === undefined)
	) {
		return {
			submission: null,
			error: `Enter ${spec.options
				.map((option) => option.label)
				.join(" or ")}.`,
		};
	}

	const uniqueOptions = Array.from(
		new Map(
			selectedOptions.flatMap((option) =>
				option ? [[option.id, option] as const] : []
			)
		).values()
	);
	if (uniqueOptions.length < spec.minSelections) {
		return {
			submission: null,
			error: `Select at least ${spec.minSelections} option${
				spec.minSelections === 1 ? "" : "s"
			}.`,
		};
	}
	if (uniqueOptions.length > spec.maxSelections) {
		return {
			submission: null,
			error: `Select no more than ${spec.maxSelections} options.`,
		};
	}

	return {
		submission: {
			response: {
				question_id: spec.questionId,
				input_kind: "options",
				selected: uniqueOptions.map((option) => option.id),
			},
			displayValue: uniqueOptions
				.map((option) =>
					getPaulOptionDisplayLabel(option, spec.options)
				)
				.join(", "),
		},
		error: null,
	};
};
